export const ASSET_CATEGORY_IDS = [
    "VEHICLE", "AIR_CONDITIONER", "REFRIGERATOR", "WASHING_MACHINE", "STOVE", "MICROWAVE", "WATER_HEATER",
    "TELEVISION", "COMPUTER", "PHONE", "PRINTER", "GENERATOR", "APPLIANCE", "DEVICE", "OTHER",
];
/**
 * The list's order and each category's name. The common objects first; then
 * the two catch-alls of their families («Otro electrodoméstico», «Otro aparato
 * electrónico»); «Otro» last: the fallback, not an option among equals.
 */
export const ASSET_CATEGORIES = [
    { id: "VEHICLE", label: "Vehículo" },
    { id: "AIR_CONDITIONER", label: "Aire acondicionado" },
    { id: "REFRIGERATOR", label: "Nevera o congelador" },
    { id: "WASHING_MACHINE", label: "Lavadora o secadora" },
    { id: "STOVE", label: "Estufa o cocina de gas" },
    { id: "MICROWAVE", label: "Microondas" },
    { id: "WATER_HEATER", label: "Calentador de agua" },
    { id: "TELEVISION", label: "Televisor" },
    { id: "COMPUTER", label: "Computadora o laptop" },
    { id: "PHONE", label: "Celular o tablet" },
    { id: "PRINTER", label: "Impresora" },
    { id: "GENERATOR", label: "Planta eléctrica" },
    { id: "APPLIANCE", label: "Otro electrodoméstico" },
    { id: "DEVICE", label: "Otro aparato electrónico" },
    { id: "OTHER", label: "Otro" },
];
/**
 * THE COLOURS AN OBJECT MAY BE, by id: what is stored and travels is the id,
 * what a person reads is the name, beside its swatch.
 *
 * A LIST AND NOT A TEXT FIELD, because «blanca», «Blanco» and «BCO» are three
 * spellings of one car, and a person picking from ten is faster than one
 * typing seven letters with a customer waiting.
 *
 * THE SWATCHES ARE THE ONE DOCUMENTED EXCEPTION TO THE THEME: a car's colour
 * is a fact about the car, like its plate, and a red car drawn in the theme's
 * `danger` would be saying something about it. The swatch is the same in
 * both themes: a white car is white in the dark.
 *
 * `outlined` MEANS «NEEDS A BORDER ON LIGHT AND ON DARK», and the phone's own
 * `theme-contrast.test.ts` is what decides which. Blanco, plateado and
 * amarillo vanish into a white card; negro vanishes into the dark one. The
 * flag cannot be per-theme: a dot does not change shape when the phone does.
 */
export const ASSET_COLORS = [
    { id: "blanco", label: "Blanco", swatch: "#F5F5F5", outlined: true },
    { id: "negro", label: "Negro", swatch: "#1F1F1F", outlined: true },
    { id: "gris", label: "Gris", swatch: "#8E8E93", outlined: false },
    { id: "plateado", label: "Plateado", swatch: "#C7C9CC", outlined: true },
    { id: "rojo", label: "Rojo", swatch: "#D32F2F", outlined: false },
    { id: "azul", label: "Azul", swatch: "#1E5BC6", outlined: false },
    { id: "verde", label: "Verde", swatch: "#2E7D32", outlined: false },
    { id: "amarillo", label: "Amarillo", swatch: "#F2C230", outlined: true },
    { id: "naranja", label: "Naranja", swatch: "#EF6C00", outlined: false },
    { id: "marron", label: "Marrón", swatch: "#6D4C41", outlined: false },
    // `outlined` IS SPELLED ON EVERY ROW, false included: a flag that is only
    // present on some of them types as absent on the others, and the two
    // clients and the contrast test all read it off an entry they found by id.
];
/** The ten ids alone, for a schema's own `z.enum`. */
export const ASSET_COLOR_IDS = ASSET_COLORS.map((color) => color.id);
export function isAssetColor(value) {
    return typeof value === "string" && ASSET_COLOR_IDS.includes(value);
}
/** The colour an id names, or null for `""` or anything off the list. */
export function assetColorOf(id) {
    return ASSET_COLORS.find((color) => color.id === id) ?? null;
}
/** How many objects one document may work on. */
export const MAX_ASSETS_PER_DOCUMENT = 10;
/** Photos of one object: the catalogue item's cap, pictures of one thing. */
export const MAX_ATTACHMENTS_PER_ASSET = 5;
export const MAX_ASSET_TEXT_LENGTH = 80;
export const MAX_ASSET_DESCRIPTION_LENGTH = 500;
/**
 * The cap, counted on the stored plate: ten covers what the Americas issue.
 */
export const MAX_ASSET_PLATE_LENGTH = 10;
function categoryLabel(category) {
    return ASSET_CATEGORIES.find((c) => c.id === category)?.label ?? "Otro";
}
/** «Otro» is called what it is; every other object, by its category. */
export function assetName(asset) {
    return asset.category === "OTHER" ? asset.otherName.trim() : categoryLabel(asset.category);
}
/** Only a vehicle carries a plate. */
export function takesPlate(category) {
    return category === "VEHICLE";
}
/** A vehicle's serial is its chassis number. */
export function serialLabel(category) {
    return takesPlate(category) ? "Chasis (VIN)" : "Serie";
}
/**
 * Upper case, no spaces, no hyphens: «PA-1234» and «pa 1234» are one car. It
 * is how the plate is STORED, so the unique index `asset_one_plate` compares
 * the column as it is.
 */
export function plateKey(raw) {
    return raw.toUpperCase().replace(/[\s-]+/g, "");
}
/**
 * What a document and its paper call the object: «Aire acondicionado ·
 * Samsung AR12 · Serie 4471», «Vehículo · Toyota Hilux · Blanco · PA1234».
 * The colour's NAME right after make and model; a stored colour off the list
 * (written before the list existed) reads as it was written. Leaves out what
 * is empty, so nothing hangs. Migration 0101 writes this same label in SQL.
 */
export function assetLabel(asset) {
    const serial = asset.serial.trim();
    return [
        assetName(asset),
        `${asset.make.trim()} ${asset.model.trim()}`.trim(),
        colorName(asset.color.trim()),
        takesPlate(asset.category) ? asset.plate.trim() : "",
        serial === "" ? "" : `${serialLabel(asset.category)} ${serial}`,
    ]
        .filter((part) => part !== "")
        .join(" · ");
}
/**
 * `assetLabel` for a row that draws the colour as its dot (`ColorDot`) beside
 * it: a colour of the list leaves the words, one off the list stays as
 * written, since no dot can draw it. A notice or a mail has no dot and says
 * `assetLabel` whole.
 */
export function assetLabelBesideDot(asset) {
    return assetLabel(isAssetColor(asset.color.trim()) ? { ...asset, color: "" } : asset);
}
/**
 * What a stranger reads of the object on its history link: name · make and
 * model · colour, as `assetLabel` writes them. No plate and no serial: they
 * identify the object to whoever the link is forwarded to.
 */
export function publicAssetLabel(asset) {
    return [assetName(asset), `${asset.make.trim()} ${asset.model.trim()}`.trim(), colorName(asset.color.trim())]
        .filter((part) => part !== "")
        .join(" · ");
}
/**
 * A label as `assetLabel` wrote it, with its colour taken back out, for a
 * screen that draws the colour as a dot (`ColorDot`) instead of a word. A
 * saved document's objects travel as their frozen label alone, so the colour
 * is read off it: the segment, never the first (the object's name), that is a
 * list colour's own word. A label with none, or one off the list, stays whole.
 */
export function assetLabelParts(label) {
    const parts = label.split(" · ");
    const at = parts.findIndex((part, index) => index > 0 && ASSET_COLORS.some((color) => color.label === part));
    if (at === -1)
        return { text: label, color: null };
    const color = ASSET_COLORS.find((one) => one.label === parts[at]).id;
    return { text: parts.filter((_, index) => index !== at).join(" · "), color };
}
function colorName(color) {
    return assetColorOf(color)?.label ?? color;
}
/** The short texts' cap sentences; the serial's is named per category (`serialWords`). */
const TEXT_WORDS = [
    ["otherName", "El nombre es"],
    ["make", "La marca es"],
    ["model", "El modelo es"],
];
/** «La serie es», or a vehicle's «El chasis (VIN) es»: the field as the form labels it (`serialLabel`). */
function serialWords(category) {
    const label = serialLabel(category);
    return takesPlate(category) ? `El ${label.charAt(0).toLowerCase()}${label.slice(1)} es` : `La ${label.toLowerCase()} es`;
}
/**
 * The one refusal of a bad object, on the server and before it on the screen.
 * Null: fine. Only what the category keeps is measured: `normalizedAsset`
 * drops the rest.
 */
export function assetProblem(input) {
    const values = normalizedAsset(input);
    if (input.category === "OTHER" && values.otherName === "")
        return { field: "otherName", message: "Escribe qué es." };
    // A vehicle is known by its plate: the database refuses one without it (0098).
    if (takesPlate(input.category) && values.plate === "")
        return { field: "plate", message: "Escribe la placa." };
    if (values.plate.length > MAX_ASSET_PLATE_LENGTH) {
        return { field: "plate", message: `La placa es de hasta ${MAX_ASSET_PLATE_LENGTH} caracteres.` };
    }
    if (values.color !== "" && !isAssetColor(values.color))
        return { field: "color", message: "Elige un color de la lista." };
    const texts = [...TEXT_WORDS, ["serial", serialWords(input.category)]];
    for (const [field, words] of texts) {
        if (values[field].length > MAX_ASSET_TEXT_LENGTH) {
            return { field, message: `${words} de hasta ${MAX_ASSET_TEXT_LENGTH} caracteres.` };
        }
    }
    if (values.description.length > MAX_ASSET_DESCRIPTION_LENGTH) {
        return { field: "description", message: `Los detalles son de hasta ${MAX_ASSET_DESCRIPTION_LENGTH} caracteres.` };
    }
    return null;
}
/** What is stored: trimmed, the plate keyed, and what the category has no place for, emptied. */
export function normalizedAsset(input) {
    return {
        category: input.category,
        otherName: input.category === "OTHER" ? (input.otherName ?? "").trim() : "",
        make: (input.make ?? "").trim(),
        model: (input.model ?? "").trim(),
        color: (input.color ?? "").trim(),
        plate: takesPlate(input.category) ? plateKey(input.plate ?? "") : "",
        serial: (input.serial ?? "").trim(),
        description: (input.description ?? "").trim(),
    };
}
/**
 * ONE PLACE FOR EVERY SENTENCE WITH THE WORD IN IT, so neither a client nor
 * the server writes «equipo» by hand: the mechanic's shop will read
 * «vehículo» everywhere the day its word arrives.
 */
export function assetSentences(w) {
    return {
        theirs: `Sus ${w.many}`,
        none: `Sin ${w.many}`,
        heading: w.One,
        addTitle: `Agregar ${w.one}`,
        search: `Buscar ${w.one}`,
        gone: `Ese ${w.one} ya no existe.`,
        ofAnotherClient: `Ese ${w.one} es de otro cliente.`,
        archived: `Ese ${w.one} está archivado.`,
        worked: `Este ${w.one} tiene trabajos anotados. No se puede borrar.`,
        onlyClients: `Sólo un cliente tiene ${w.many}.`,
        tooMany: `Un documento lleva hasta ${MAX_ASSETS_PER_DOCUMENT} ${w.many}.`,
        needsCustomer: `Elige el cliente antes de agregar un ${w.one}.`,
        notHere: `Este negocio no anota ${w.many} de sus clientes.`,
        photosFull: `Este ${w.one} ya tiene ${MAX_ATTACHMENTS_PER_ASSET} fotos.`,
        deleteQuestion: `¿Borrar este ${w.one}? Se borran también sus fotos.`,
        notOnDocument: `Ese ${w.one} no está en este documento.`,
        notesNeedTwo: `Lo que se hizo va por ${w.one} sólo con dos ${w.many} o más.`,
        needsLine: `Cada ${w.one} lleva al menos una línea: agrégale algo o quítalo.`,
        byOneQuestion: `¿Hay más de un ${w.one}? Anota a cuál va cada cosa.`,
        unrelated: `No relacionado a un ${w.one}`,
        newOne: `+ Agregar un ${w.one} nuevo`,
        whichOne: `¿De qué ${w.one} es?`,
        searchTheirs: `Buscar ${w.one} del cliente`,
        assign: `Asignar a ${w.one}`,
        noneOnPurpose: `No es de ningún ${w.one}`,
        noPhotosYet: `Agrégale fotos como evidencia de lo que trabajaste en este ${w.one}: el antes y el después.`,
        otherPhotos: `Las que no son de un ${w.one}, como un recibo o el lugar del trabajo.`,
        oneUnassigned: `1 línea no dice de qué ${w.one} es.`,
        manyUnassigned: `líneas no dicen de qué ${w.one} es.`,
        notThere: `Ese ${w.one} ya no está.`,
        manyNotThere: `${w.many} ya no están.`,
    };
}
/** The server's refusal of a repeated plate. */
export function plateTakenMessage(plate, partyName) {
    return `Ya existe la placa ${plate}, de ${partyName}.`;
}
/** The form's warning while the plate is typed. */
export function plateHintMessage(plate, partyName) {
    return `Ya existe ${plate}, de ${partyName}`;
}
